import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Target, Eye, Users, Shield, Lightbulb, ArrowRight, 
  ChevronDown, CheckCircle, Award, Globe
} from 'lucide-react';
import './About.css';

const About: React.FC = () => {
  const [activeAccordion, setActiveAccordion] = useState<number | null>(0);

  const approachItems = [
    {
      icon: <Users size={24} />,
      title: 'Deep Domain Knowledge',
      content: 'Teams with decades of life sciences, pharma and device experience to ensure regulatory compliance and quality standards.',
      examples: ['Pre-configured templates for GMP, GLP, GCP compliance', 'Validation-ready systems with audit trails']
    },
    {
      icon: <Shield size={24} />,
      title: 'Compliance-First Engineering',
      content: 'Built to match GxP, FDA 21 CFR Part 11, ISO 9001, EU Annex 11, GDPR and data privacy expectations with auditable systems.',
      examples: ['21 CFR Part 11 compliant e-signatures with multi-step approval workflows', 'Built-in audit trails and exportable logs for regulatory inspections']
    },
    {
      icon: <Lightbulb size={24} />,
      title: 'Innovation at Scale',
      content: 'Cloud-native architecture, analytics and AI-readiness to turn quality data into actionable insights.',
      examples: ['Predictive compliance alerts and risk scoring', 'Smart document classification and semantic search']
    },
  ];

  const values = [
    { icon: <Award size={32} />, title: 'Excellence', description: 'Delivering best-in-class solutions' },
    { icon: <Shield size={32} />, title: 'Integrity', description: 'Transparent and ethical practices' },
    { icon: <Users size={32} />, title: 'Collaboration', description: 'Partnership with our clients' },
    { icon: <Globe size={32} />, title: 'Innovation', description: 'Continuous improvement mindset' },
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="about-hero-content">
            <span className="badge">About Aurexa</span>
            <h1>Empowering Regulated Industries through Digital Transformation</h1>
            <p>
              Aurexa Technologies (P) Ltd is a domain-first technology firm helping regulated 
              industries replace brittle manual processes with auditable, automated systems.
            </p>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="section">
        <div className="container">
          <div className="about-grid">
            <div className="about-text">
              <h2>Building Systems That Pass Inspection and Scale With Growth</h2>
              <p>
                We combine regulatory domain expertise with modern cloud engineering to deliver 
                systems that pass inspection and scale with growth. Our approach ensures that 
                compliance becomes an enabler, not a barrier, to your business success.
              </p>
              <p>
                Our products — including QC-Metric — connect documents, training and corrective 
                workflows so quality becomes proactive instead of reactive. We understand that 
                in regulated industries, the cost of non-compliance far exceeds the investment 
                in proper systems.
              </p>
              <div className="about-stats">
                <div className="stat">
                  <span className="stat-number">100+</span>
                  <span className="stat-label">Deployments</span>
                </div>
                <div className="stat">
                  <span className="stat-number">50+</span>
                  <span className="stat-label">Enterprise Clients</span>
                </div>
                <div className="stat">
                  <span className="stat-number">99.9%</span>
                  <span className="stat-label">Uptime SLA</span>
                </div>
              </div>
            </div>
            <div className="about-image">
              <div className="about-image-placeholder">
                <img src="/logo.png" alt="Aurexa Team" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <span className="badge">Our Approach</span>
            <h2>How We Deliver Excellence</h2>
            <p>
              Three pillars that define our commitment to your success.
            </p>
          </div>
          <div className="approach-accordion">
            {approachItems.map((item, index) => (
              <div 
                key={index} 
                className={`accordion-item ${activeAccordion === index ? 'active' : ''}`}
              >
                <button 
                  className="accordion-header"
                  onClick={() => setActiveAccordion(activeAccordion === index ? null : index)}
                >
                  <div className="accordion-icon">{item.icon}</div>
                  <span className="accordion-title">{item.title}</span>
                  <ChevronDown className="accordion-arrow" size={20} />
                </button>
                <div className="accordion-content">
                  <p>{item.content}</p>
                  <ul className="accordion-examples">
                    {item.examples.map((example, idx) => (
                      <li key={idx}>
                        <CheckCircle size={16} />
                        {example}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section">
        <div className="container">
          <div className="mission-vision-grid">
            <div className="mv-card mission">
              <div className="mv-icon">
                <Target size={40} />
              </div>
              <h3>Our Mission</h3>
              <p className="mv-main">
                To empower regulated organizations to adopt digital workflows confidently 
                through reliable, compliant and scalable solutions that improve quality, 
                efficiency and cross-team collaboration.
              </p>
              <ul className="mv-points">
                <li><CheckCircle size={16} /> Reliable solutions</li>
                <li><CheckCircle size={16} /> Compliant processes</li>
                <li><CheckCircle size={16} /> Scalable architecture</li>
              </ul>
            </div>
            <div className="mv-card vision">
              <div className="mv-icon">
                <Eye size={40} />
              </div>
              <h3>Our Vision</h3>
              <p>
                A world where organizations in highly regulated sectors operate with modern, 
                enterprise-grade technology that accelerates quality outcomes without 
                compromising compliance or security.
              </p>
              <p className="mv-highlight">
                Quality systems should be integrated, proactive and business-enabling.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <span className="badge">Our Values</span>
            <h2>What Drives Us</h2>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-icon">{value.icon}</div>
                <h4>{value.title}</h4>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Work With Us?</h2>
            <p>
              Let's discuss how Aurexa can help transform your quality management processes.
            </p>
            <div className="cta-buttons">
              <Link to="/demo" className="btn btn-primary">
                Book a Demo <ArrowRight size={18} />
              </Link>
              <Link to="/contact" className="btn btn-outline-white">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
